import { CheckCircle, Shield, User as UserIcon, X } from "lucide-react";
import type { AdminUser } from "../../hooks/useAdminUser";
import { Modal } from "../ui/Modal";

// Modal de perfil del user logueado. Read-only — la edición de
// email/rol/sucursal pasa siempre por el panel del super_admin
// (/admin/usuarios). Evita que un user "experimente" cambiándose a
// sí mismo y se deslogue.
//
// Se cierra con Escape o click en el backdrop.

type Props = {
  open: boolean;
  user: AdminUser;
  onClose: () => void;
};

export function ProfileModal({ open, user, onClose }: Props) {
  const isSuper = user.role === "super_admin";
  const RoleIcon = isSuper ? Shield : UserIcon;
  // Color del avatar según rol. Mantener en sync con UserMenu para que
  // el trigger y el modal usen el mismo color.
  const roleColor = isSuper ? "bg-red-500/20 text-red-300" : "bg-blue-500/20 text-blue-300";
  const roleBadge = isSuper ? "bg-red-500/15 text-red-300" : "bg-blue-500/15 text-blue-300";

  const memberSince = user.created_at
    ? new Date(user.created_at * 1000).toLocaleDateString("es-PY", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Desconocido";

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnBackdrop
      maxWidthClass="max-w-md"
      labelledBy="profile-title"
    >
      <div className="mb-4 flex items-start justify-between">
        <h2 id="profile-title" className="font-heading text-xl font-bold">
          Tu perfil
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 opacity-60 hover:bg-white/10 hover:opacity-100"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

        {/* Avatar grande del rol */}
        <div className="mb-4 flex justify-center">
          <div
            className={`flex h-20 w-20 items-center justify-center rounded-full ${roleColor}`}
          >
            <RoleIcon className="h-10 w-10" />
          </div>
        </div>

        {/* Datos */}
        <div className="space-y-3">
          <Field label="Email">
            <div className="flex items-center gap-2">
              <span className="break-all font-mono text-sm">{user.email}</span>
              <span className="flex shrink-0 items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-300">
                <CheckCircle className="h-3 w-3" />
                Verificado
              </span>
            </div>
          </Field>

          <Field label="Rol">
            <span className={`inline-block rounded px-2 py-0.5 text-xs ${roleBadge}`}>
              {isSuper ? "Super admin" : "Admin de sucursal"}
            </span>
          </Field>

          <Field label="Sucursal">
            <span className="text-sm">
              {user.sucursal_nombre ?? (
                <span className="opacity-60 italic">
                  {isSuper ? "Todas las sucursales" : "Sin asignar"}
                </span>
              )}
            </span>
          </Field>

          <Field label="Miembro desde">
            <span className="text-sm">{memberSince}</span>
          </Field>
        </div>

        {/* Footer — solo para super_admin tiene sentido el link al
            panel de usuarios (admin común no tiene acceso a esa ruta).
            Para admin común no mostramos nada: la edición de su propio
            perfil pasa por su super_admin y no es algo que tenga que
            hacer él mismo desde este modal. */}
        {isSuper && (
          <p className="mt-5 rounded-lg bg-blue-500/10 px-3 py-2 text-xs text-blue-300">
            Para gestionar usuarios (crear, cambiar rol, asignar sucursal)
            andá al panel{" "}
            <code className="rounded bg-white/10 px-1">/admin/usuarios</code>.
          </p>
        )}
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white/5 px-4 py-3">
      <div className="mb-1 text-[10px] uppercase tracking-wider opacity-50">
        {label}
      </div>
      {children}
    </div>
  );
}
